import React, {
  useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { parse } from 'qs';
import { Form, message as antdMessage } from 'antd';
import {
  ArrowRight,
  CalendarCheck,
  ChatCircleText,
  Key,
  PhoneCall,
  Sparkle,
} from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import Input from '../../components/Input';
import Button from '../../components/Button';
import './newLogin.scss';
import Checkbox from '../../components/Checkbox';
import Link from '../../components/Link';
import COLORS from '../../util/constants/colors';
import { MicrosoftSvg } from '../../assets/img/redesign/svgPaths';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  login,
  resendOtp as resendOtpAction,
  redirectToLogin as redirectToLoginAction,
} from '../../actions/auth';
import { openModal } from '../../actions/modal';
import { captureAnalytics } from '../../actions/preload';
import { validSendEmail as validEmail } from '../../global/validation';
import { OTP_DELIVERY, loadOtpDelivery } from '../../global/otpDelivery';
import {
  LOGIN_METHODS,
  forgetRememberedSignIn,
  loadRememberedSignIn,
  rememberEmail,
} from '../../global/lastLoginMethod';
import PrivacyPolicy from '../PrivacyPolicy';
import NewHeader from '../NewHeader';
import { startOmniauthSignIn } from '../../util/omniauthSignIn';
import {
  SSO_RETURN_PATH_KEY,
  isIosAppSignInRedirect,
  loginPrinterSignInRedirect,
} from '../../util/appSignIn';
import {
  NOT_SET_UP_MESSAGE,
  discoverSsoSignIn,
  loginReturnPath,
  ssoErrorMessage,
  startSsoSignIn,
} from '../../util/ssoSignIn';

const SSO_EMAIL_REQUIRED = 'Enter your work email to sign in with SSO.';

// On the sign-in option this browser used last (lastLoginMethod).
const LastUsedBadge = () => <span className="last-used-badge">Last used</span>;

const RECAPTURE_DEMO_URL = 'https://meetings.hubspot.com/zain3/15-minute-newfeature-demo?feature=recapture';

const RecaptureLoginPromo = ({ captureAnalyticsEvent }) => (
  <aside className="recapture-login-promo" aria-labelledby="recapture-login-title">
    <div className="recapture-login-glow recapture-login-glow--top" />
    <div className="recapture-login-glow recapture-login-glow--bottom" />
    <div className="recapture-login-orbit" aria-hidden="true">
      <span className="recapture-login-orbit-dot recapture-login-orbit-dot--one" />
      <span className="recapture-login-orbit-dot recapture-login-orbit-dot--two" />
    </div>

    <div className="recapture-login-promo-content">
      <div className="recapture-login-eyebrow">
        <Sparkle size={16} weight="fill" />
        <span>New · Medsender Recapture</span>
      </div>

      <h1 id="recapture-login-title" className="recapture-login-title">
        Bring overdue patients back—without adding work for your team.
      </h1>

      <p className="recapture-login-description">
        Recapture uses AI voice and SMS to reach patients who are due back,
        start the conversation, and help turn intent into booked appointments.
      </p>

      <div className="recapture-login-benefits">
        <div className="recapture-login-benefit">
          <span className="recapture-login-benefit-icon">
            <PhoneCall size={20} weight="fill" />
          </span>
          <span>AI voice that converses—not just notifies</span>
        </div>
        <div className="recapture-login-benefit">
          <span className="recapture-login-benefit-icon">
            <ChatCircleText size={20} weight="fill" />
          </span>
          <span>Automated SMS outreach and follow-up</span>
        </div>
        <div className="recapture-login-benefit">
          <span className="recapture-login-benefit-icon">
            <CalendarCheck size={20} weight="fill" />
          </span>
          <span>Scheduling links and live front-desk handoff</span>
        </div>
      </div>

      <div className="recapture-login-actions">
        <a
          className="recapture-login-cta"
          href={RECAPTURE_DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => captureAnalyticsEvent('recapture_login_demo_clicked', {
            placement: 'login_promo',
          })}
        >
          See Recapture in action
          <ArrowRight size={18} weight="bold" />
        </a>
        <span className="recapture-login-demo-note">Personalized 15-minute demo</span>
      </div>
    </div>

    <div className="recapture-login-result" aria-hidden="true">
      <div className="recapture-login-result-icon">
        <CalendarCheck size={22} weight="fill" />
      </div>
      <div>
        <span className="recapture-login-result-label">Patient journey</span>
        <strong>Reached → Engaged → Booked</strong>
      </div>
    </div>
  </aside>
);

RecaptureLoginPromo.propTypes = {
  captureAnalyticsEvent: PropTypes.func,
};

RecaptureLoginPromo.defaultProps = {
  captureAnalyticsEvent: captureAnalytics,
};

const LoginContent = ({
  email,
  setEmail,
  password,
  setPassword,
  rememberMe,
  setRememberMe,
  handleLogin,
  errorStatus,
  loginError,
  message,
  startMicrosoftLogin,
  startSsoLogin,
  ssoMessage,
  ssoLoading,
  captureAnalyticsEvent,
  showSignUp,
  forgotPasswordSearch,
  rememberedEmail,
  lastLoginMethod,
}) => {
  const breakpoint = useBreakpoint();
  // A failed sign-in's message wins; otherwise what single sign-on has to say.
  const shownMessage = (loginError && message) || ssoMessage;
  // The option this browser signed in with last (for the remembered email) gets a "Last used" tag.
  // The order never changes, so the buttons stay where people expect them.
  const ssoLastUsed = Boolean(rememberedEmail) && lastLoginMethod === LOGIN_METHODS.saml;
  const microsoftLastUsed = Boolean(rememberedEmail) && lastLoginMethod === LOGIN_METHODS.microsoft;

  const microsoftButton = (
    <div className="last-used-method">
      <Button
        fullWidth
        block
        type="secondary"
        label="Log in with Microsoft"
        size="large"
        leftIcon={MicrosoftSvg}
        onClick={() => {
          // Fire before startMicrosoftLogin, which submits a form and navigates away.
          captureAnalyticsEvent('microsoft_sso_login_clicked', {
            placement: 'login_form',
            ...(microsoftLastUsed && { last_used: true }),
          });
          startMicrosoftLogin();
        }}
      />
      {microsoftLastUsed && <LastUsedBadge />}
    </div>
  );
  const ssoButton = (
    <div className="last-used-method">
      <Button
        fullWidth
        block
        type="secondary"
        label="Log in with SSO"
        size="large"
        leftIcon={<Key size={24} />}
        loading={ssoLoading}
        onClick={() => {
          captureAnalyticsEvent('saml_sso_login_clicked', {
            placement: 'login_form',
            ...(ssoLastUsed && { last_used: true }),
          });
          startSsoLogin();
        }}
      />
      {ssoLastUsed && <LastUsedBadge />}
    </div>
  );

  return (
    <>
      <Typography variant="h3">Log in</Typography>
      {shownMessage && (
        // Announced to screen readers: it can appear after a click (SSO discovery) with no page change.
        <div role="alert">
          <Typography variant="subtitleTwo" color={COLORS.ERROR_LITE}>
            {shownMessage}
          </Typography>
        </div>
      )}
      {/* Form.Item owns its field's value, so the remembered email is seeded here; the
          Input's own value prop alone left the field empty while the page still used it. */}
      <Form name="login" onFinish={handleLogin} autoComplete="on" initialValues={{ username: email }}>
        <div className="fields">
          <Form.Item name="username">
            <Input
              className="email"
              label="Email"
              value={email}
              onChange={e => setEmail(String(e.target.value.replace(' ', '').toLowerCase()))}
              status={errorStatus.emailMsg && 'error'}
              description={errorStatus.emailMsg}
            />
          </Form.Item>
          <Form.Item name="password">
            <Input
              label="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              status={errorStatus.passwordMsg && 'error'}
              description={errorStatus.passwordMsg}
              password
            />
          </Form.Item>
        </div>
        <div className="remember-forgot">
          <Checkbox
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
          >
            <Typography variant="text" size="medium">
              Remember me
            </Typography>
          </Checkbox>
          <Link bar href={`/app/forgot_password${forgotPasswordSearch}`} label="Forgot password?" />
        </div>
        <Button
          block
          type="primary"
          label="Log in"
          size="large"
          htmlType="submit"
        />
      </Form>

      <div className="or-divider">
        <Typography variant="text" size="small">
          or
        </Typography>
      </div>
      {microsoftButton}
      {ssoButton}
      {showSignUp && breakpoint === 'xsmall' && (
        <>
          <Typography variant="text" size="medium" bold>
            Want to demo Medsender?
          </Typography>
          <Button
            label="Create free account"
            href="https://www.medsender.com/sign-up?ref=loginpage"
            size="medium"
          />
        </>
      )}
    </>
  );
};

LoginContent.propTypes = {
  captureAnalyticsEvent: PropTypes.func,
  showSignUp: PropTypes.bool,
  forgotPasswordSearch: PropTypes.string,
  startSsoLogin: PropTypes.func,
  ssoMessage: PropTypes.string,
  ssoLoading: PropTypes.bool,
  rememberedEmail: PropTypes.string,
  lastLoginMethod: PropTypes.string,
};

LoginContent.defaultProps = {
  captureAnalyticsEvent: captureAnalytics,
  showSignUp: true,
  forgotPasswordSearch: '',
  startSsoLogin: () => {},
  ssoMessage: null,
  ssoLoading: false,
  rememberedEmail: null,
  lastLoginMethod: null,
};

// The access code is for people a record was sent to, not for staff signing in to an app; in the
// iOS app's sign-in sheet its page would lead to the ordinary login, which cannot finish the
// sign-in. There the consent line names the policies without linking them (PrivacyPolicy).
const LoginFooter = ({ iosAppSignIn }) => (
  <div className="login-footer">
    {!iosAppSignIn && (
      <Typography variant="text" size="small">
        Have an access code?&nbsp;
        {' '}
        <Link
          href="/app/access_code"
          label="Enter it here."
          style={{ color: COLORS.TEXT_PRIMARY_LITE }}
        />
      </Typography>
    )}
    <PrivacyPolicy iosAppSignIn={iosAppSignIn} />
  </div>
);

LoginFooter.propTypes = {
  iosAppSignIn: PropTypes.bool,
};

LoginFooter.defaultProps = {
  iosAppSignIn: false,
};

const OtpContent = ({
  otpCode,
  setOtpCode,
  sentViaEmail,
  otpDelivery,
  authenticatorAppEnabled,
  otpChecked,
  setOtpChecked,
  message,
  handleSubmit,
  checkPhoneNumber,
}) => {
  const usesAuthenticatorApp = otpDelivery === OTP_DELIVERY.authenticatorApp;

  return (
    <>
      <Typography variant="subtitleTwo">One Last Step</Typography>
      {usesAuthenticatorApp ? (
        <Typography variant="text" size="large">
          We have detected that you are signing in from a new browser.
          <br />
          Open your authenticator app and enter the 6-digit code for Medsender.
        </Typography>
      ) : (
        <Typography variant="text" size="large">
          We have detected that you are signing in from a new browser.
          <br />
          For your security, a verification code has been sent to your
          {' '}
          {sentViaEmail ? 'email' : 'sms'}
          .
          Please enter it below.
          {authenticatorAppEnabled && (
            <>
              <br />
              You can also enter a code from your authenticator app.
            </>
          )}
        </Typography>
      )}
      <Form
        name="otpStep"
        onFinish={handleSubmit}
      >
        <Input
          label="Verification Code:"
          placeholder="Enter verification code here"
          value={otpCode}
          onChange={e => setOtpCode(e.target.value)}
          status={message && 'error'}
          description={message}
          inputMode="numeric"
          autoComplete="one-time-code"
        />
        <div className="dont-ask-again">
          <Checkbox
            checked={otpChecked}
            onChange={e => setOtpChecked(e.target.checked)}
          >
            <Typography variant="text" size="medium">
              Don't ask again on this computer
            </Typography>
          </Checkbox>
        </div>
        <Button
          block
          type="primary"
          label="Log in"
          size="large"
          htmlType="submit"
        />
      </Form>
      {checkPhoneNumber()}
    </>
  );
};

OtpContent.propTypes = {
  otpCode: PropTypes.string.isRequired,
  setOtpCode: PropTypes.func.isRequired,
  sentViaEmail: PropTypes.bool,
  otpDelivery: PropTypes.oneOf([...Object.values(OTP_DELIVERY), null]),
  authenticatorAppEnabled: PropTypes.bool,
  otpChecked: PropTypes.bool.isRequired,
  setOtpChecked: PropTypes.func.isRequired,
  message: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  checkPhoneNumber: PropTypes.func.isRequired,
};

OtpContent.defaultProps = {
  sentViaEmail: true,
  otpDelivery: null,
  authenticatorAppEnabled: false,
  message: '',
};

const NewLogin = ({ ...props }) => {
  const [email, setEmail] = useState(localStorage.getItem('m8_email') || '');
  const [password, setPassword] = useState('');
  const [errorStatus, setErrorStatus] = useState({});
  const [otpCode, setOtpCode] = useState('');
  const [otpData, setOtpData] = useState(() => ({
    email: localStorage.getItem('email'),
    needs2FA: localStorage.getItem('needs2FA'),
    otp_auth_token: localStorage.getItem('otp_auth_token'),
    phone_number: localStorage.getItem('phone_number'),
    // How the pending code was delivered. Kept in storage only: the page
    // remounts and the auth reducer resets on every step, resend and wrong code.
    ...loadOtpDelivery(),
  }));
  const [rememberMe, setRememberMe] = useState(true);
  const [otpChecked, setOtpChecked] = useState(true);
  // Why a SAML sign-in came back here (?ssoError=, stripped from the URL on mount), or why
  // "Log in with SSO" could not start. Read once: the page remounts on every login attempt.
  const [ssoMessage, setSsoMessage] = useState(() => (
    ssoErrorMessage(parse(props.location.search, { ignoreQueryPrefix: true }).ssoError)
  ));
  const [ssoLoading, setSsoLoading] = useState(false);
  // Remember me's email and how it last signed in, for the "Last used" tag.
  const [rememberedSignIn] = useState(loadRememberedSignIn);
  const breakpoint = useBreakpoint();

  const { location, history } = props;
  const params = parse(location.search, { ignoreQueryPrefix: true });
  // The printer sign-in page this login is on its way to (Gibraltar's or the Medsender iOS app's):
  // its own ?redirect=, or after a failed Microsoft sign-in whose return lost it, the page that
  // sign-in started from (kept by startMicrosoftLogin). Forgot password, a failed Microsoft sign-in
  // and a Microsoft sign-in's return keep it, so the login still finishes that sign-in.
  const printerSignInRedirect = loginPrinterSignInRedirect(location.search);
  const redirectSearch = printerSignInRedirect !== null
    ? `?redirect=${encodeURIComponent(printerSignInRedirect)}`
    : '';
  // The iOS app's sign-in sheet has no account creation or marketing at any width, and a consent
  // line without links. Gibraltar's login is the ordinary login.
  const iosAppSignIn = isIosAppSignInRedirect(printerSignInRedirect);

  const openFailedSSODialog = (invalidDomain) => {
    const { openModal: openDialog } = props;
    const modalType = {
      data: { invalidDomain, iosAppSignIn },
      type: 'EXISTING_ACCOUNT',
    };
    openDialog(modalType);
  };

  useEffect(() => {
    localStorage.removeItem('email');
    localStorage.removeItem('needs2FA');

    if (params.failedSSO) {
      // Put the printer sign-in redirect back, so a password login afterwards still finishes it.
      history.replace(`/login${redirectSearch}`);
      const { invalidDomain } = params;
      openFailedSSODialog(invalidDomain);
    } else if (params.ssoError) {
      setSsoMessage(ssoErrorMessage(params.ssoError));
      history.replace(`/login${redirectSearch}`);
    }
  }, [location, history]);

  const resendOtp = (otpType) => {
    const { resendOtp } = props;
    resendOtp({
      otp_auth_token: otpData.otp_auth_token,
      send_otp_via_sms: otpType === 'sms',
      // The address this sign-in is for. The form's `email` is the remembered
      // address, which is blank when "Remember me" was unchecked, and the
      // server rejects a resend without the matching email.
      email: otpData.email || email,
    });
  };

  const hasPhoneNumber = Boolean(otpData.phone_number && otpData.phone_number !== 'null');
  const { otpDelivery, authenticatorAppEnabled } = otpData;
  // The SPA's password step never asks for SMS and every resend records its
  // channel, so anything but a recorded text (an older backend included) is email.
  const sentViaEmail = otpDelivery !== OTP_DELIVERY.sms;

  const requestFallbackCode = (otpType) => {
    props.captureAnalyticsEvent('login_otp_fallback_requested', {
      channel: otpType,
      placement: 'authenticator_app_prompt',
    });
    resendOtp(otpType);
  };

  const checkPhoneNumber = () => {
    if (otpDelivery === OTP_DELIVERY.authenticatorApp) {
      return (
        <div className="otp-fallback-links">
          <Link label="Email me a code instead" onClick={() => requestFallbackCode('email')} />
          {hasPhoneNumber && (
            <Link label="Text me a code instead" onClick={() => requestFallbackCode('sms')} />
          )}
        </div>
      );
    }
    if (hasPhoneNumber) {
      if (sentViaEmail) {
        return (
          <Link
            label={`Send to cell phone number ending with ${otpData.phone_number.slice(-4)} instead`}
            onClick={() => resendOtp('sms')}
          />
        );
      }
      return (
        <Link label="Send email instead" onClick={() => resendOtp('email')} />
      );
    }
    return '';
  };

  const handleLogin = () => {
    const inputValid = validateInput();
    setErrorStatus(inputValid);
    setSsoMessage(null);

    // If input is okay and 2FA is required make the request
    if (inputValid.error === false && otpData.needs2FA) {
      props.login({
        email: otpData.email,
        // Authenticator apps often show the code as "123 456".
        otp_attempt: otpCode.replace(/\s/g, ''),
        otp_auth_token: otpData.otp_auth_token,
        remember_2fa: otpChecked,
      });

      // If input is okay, continue with request
    } else if (inputValid.error === false) {
      props.login(
        {
          email,
          password,
        },
        rememberMe,
      );
    }
  };

  const validateInput = () => {
    let errorStatus = {
      error: false,
      emailMsg: '',
      passwordMsg: '',
      otpErrorMsg: '',
    };
    // Sets the correct form validation error message
    // => Checks if an otp code was entered, gives an error for an empty string
    // or a string of only white spaces
    if (otpData.needs2FA && (!otpCode || !otpCode.trim().length)) {
      errorStatus = {
        ...errorStatus,
        error: true,
        otpErrorMsg: 'Verification code cannot be empty.',
      };
    } else if (!otpData.needs2FA) {
      if (!validEmail(email)) {
        errorStatus = {
          ...errorStatus,
          error: true,
          emailMsg: 'A valid email is required',
        };
      }
      if (!password) {
        errorStatus = {
          ...errorStatus,
          error: true,
          passwordMsg: 'Password cannot be empty',
        };
      }
    }
    return errorStatus;
  };

  const saveCurrentUrl = () => {
    const currentURL = new URL(window.location.href);
    const redirectPath = decodeURIComponent(
      currentURL.searchParams.get('redirect'),
    );
    sessionStorage.setItem(SSO_RETURN_PATH_KEY, redirectPath);
  };

  const startMicrosoftLogin = () => {
    saveCurrentUrl();
    if (!startOmniauthSignIn('microsoft_graph_auth', { origin: printerSignInRedirect ?? '' })) {
      antdMessage.error(
        'Unable to start Microsoft sign-in. Please refresh the page and try again.',
      );
    }
  };

  // "Log in with SSO": the typed (or remembered) email finds the organization's SAML sign-in.
  const startSsoLogin = async (ssoEmail = email) => {
    setSsoMessage(null);
    if (!validEmail(ssoEmail)) {
      setErrorStatus({
        error: true, emailMsg: SSO_EMAIL_REQUIRED, passwordMsg: '', otpErrorMsg: '',
      });
      return;
    }

    setErrorStatus({});
    setSsoLoading(true);
    const { redirectUrl, error } = await discoverSsoSignIn(ssoEmail, printerSignInRedirect);
    if (redirectUrl) {
      // As a password sign-in does (login's rememberEmail): unchecked, the next person sees an
      // empty login rather than this account.
      if (rememberMe) rememberEmail(ssoEmail);
      else forgetRememberedSignIn();
      // Stays loading: the browser is on its way to the organization's sign-in.
      if (startSsoSignIn(redirectUrl, { returnPath: loginReturnPath() })) return;
    }
    setSsoLoading(false);
    setSsoMessage(error || NOT_SET_UP_MESSAGE);
  };

  return (
    <div className="new-login">
      <Helmet>
        <title>Login - Medsender</title>
        <meta name="description" content="Secure log in to Medsender" />
      </Helmet>
      <div className="login-body">
        {!iosAppSignIn && ['medium', 'large'].includes(breakpoint) && <RecaptureLoginPromo />}
        <div className="login-content">
          {otpData.needs2FA ? (
            <OtpContent
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              sentViaEmail={sentViaEmail}
              otpDelivery={otpDelivery}
              authenticatorAppEnabled={authenticatorAppEnabled}
              otpChecked={otpChecked}
              setOtpChecked={setOtpChecked}
              message={props.message}
              handleSubmit={handleLogin}
              checkPhoneNumber={checkPhoneNumber}
            />
          ) : (
            <LoginContent
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              rememberMe={rememberMe}
              setRememberMe={setRememberMe}
              handleLogin={handleLogin}
              errorStatus={errorStatus}
              loginError={props.error}
              message={props.message}
              startMicrosoftLogin={startMicrosoftLogin}
              startSsoLogin={startSsoLogin}
              ssoMessage={ssoMessage}
              ssoLoading={ssoLoading}
              showSignUp={!iosAppSignIn}
              forgotPasswordSearch={redirectSearch}
              rememberedEmail={rememberedSignIn.email}
              lastLoginMethod={rememberedSignIn.method}
              captureAnalyticsEvent={props.captureAnalyticsEvent}
            />
          )}
          <div style={{ height: '32px', alignSelf: 'stretch' }} />
          <LoginFooter iosAppSignIn={iosAppSignIn} />
        </div>
      </div>
    </div>
  );
};

NewLogin.propTypes = {
  message: PropTypes.string,
  login: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  resendOtp: PropTypes.func.isRequired,
  redirectToLogin: PropTypes.func.isRequired,
  email: PropTypes.string,
  new_otp_auth_token: PropTypes.string,
  location: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  captureAnalyticsEvent: PropTypes.func,
};

NewLogin.defaultProps = {
  captureAnalyticsEvent: captureAnalytics,
};

export default connect(
  state => ({
    error: state.auth.error,
    data: state.auth.data,
    message: state.auth.message,
    new_otp_auth_token: state.auth.otp_auth && state.auth.otp_auth.otp_auth_token,
    email: state.auth.otp_auth && state.auth.otp_auth.email,
    sentViaEmail: state.auth.sentViaEmail,
  }),
  {
    login,
    resendOtp: resendOtpAction,
    redirectToLogin: redirectToLoginAction,
    openModal,
  },
)(NewLogin);

export {
  NewLogin, RecaptureLoginPromo, LoginContent, OtpContent, RECAPTURE_DEMO_URL,
};
